import axios from 'axios';
import { EmailFormData } from './vitrual-assistant.type';

const aiAxios = axios.create();

export const sendEmail = async (
    serviceUrl: string,
    assistantInbox: string,
    data: EmailFormData
): Promise<{ message: string }> => {
    const res = await axios.post<{ message: string }>(serviceUrl, { ...data, assistantInbox });
    return res.data;
};

export interface AiResponse {
    answer: string;
    file_url: string | null;
}

export interface AskAIParams {
    query: string;
    thread_id: string;
    file?: File | Blob;
}


export const askAI = async ({
    query,
    thread_id,
    file,
}: AskAIParams): Promise<AiResponse> => {
    const formData = new FormData();

    formData.append('query', query);
    formData.append('thread_id', thread_id);

    if (file) {
        formData.append('file', file);
    }

    const { data } = await aiAxios.post<AiResponse>(
        '/services/isnaportal/open-api/nj-keden/process-sql',
        formData
    );

    return data;
};


const NJ_KEDEN_API =
    '/services/isnaportal/open-api/nj-keden/process-sql';

const NJ_KEDEN_SESSION_KEY = 'njKedenSessionId';

export const getNjKedenSessionId = (): string => {
    let sessionId = sessionStorage.getItem(NJ_KEDEN_SESSION_KEY);

    if (!sessionId) {
        sessionId = crypto.randomUUID();
        sessionStorage.setItem(NJ_KEDEN_SESSION_KEY, sessionId);
    }

    return sessionId;
};

export interface AskAIStreamParams {
    query: string;
    threadId?: string;
    file?: File | Blob;
    signal?: AbortSignal;
}

export const askAIStream = async ({
    query,
    threadId,
    file,
    signal,
}: AskAIStreamParams): Promise<Response> => {
    const formData = new FormData();

    formData.append('query', query);
    formData.append('mode', 'sql');

    if (threadId) {
        formData.append('thread_id', threadId);
    }

    if (file) {
        formData.append('file', file);
    }

    const response = await fetch(`${NJ_KEDEN_API}/stream`, {
        method: 'POST',
        headers: {
            Accept: 'text/event-stream',
            'X-Nj-Keden-Session': getNjKedenSessionId(),
        },
        body: formData,
        signal,
    });

    if (!response.ok) {
        throw new Error(`AI stream HTTP ${response.status}`);
    }

    const contentType = response.headers.get('content-type') || '';

    if (!contentType.toLowerCase().includes('text/event-stream')) {
        throw new Error('Expected text/event-stream response');
    }

    return response;
};

export const stopAIStream = async (
    messageId: string
): Promise<void> => {
    const response = await fetch(
        `${NJ_KEDEN_API}/stream/${encodeURIComponent(messageId)}/stop`,
        {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'X-Nj-Keden-Session': getNjKedenSessionId(),
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Stop stream HTTP ${response.status}`);
    }
};

export const downloadAIFile = async (
    fileId: string,
    fileName: string
): Promise<void> => {
    const response = await fetch(
        `${NJ_KEDEN_API}/files/${encodeURIComponent(fileId)}`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/octet-stream',
                'X-Nj-Keden-Session': getNjKedenSessionId(),
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Download HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};

export const getAIHealth = async (): Promise<boolean | null> => {
    const response = await fetch(`${NJ_KEDEN_API}/health`, {
        headers: {
            Accept: 'application/json',
            'X-Nj-Keden-Session': getNjKedenSessionId(),
        },
    });

    if (!response.ok) {
        throw new Error(`Health HTTP ${response.status}`);
    }

    const data = await response.json();

    return typeof data?.modes?.sql?.available === 'boolean'
        ? data.modes.sql.available
        : null;
};
